import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Household, HouseholdStatus } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { localStorageGet, LocalStorageKey } from '../../data/client_local_storage.ts';
import { synchronizeHouseholdWithServer, useHousehold } from '../../data/use_household.tsx';
import { isInsideMobileApp } from '../../util/billing.ts';
import { LogMessages } from '../../util/log_messages.tsx';
import { storeOptimisticSubscription } from '../../util/optimistic_subscription.ts';
import { Spinner } from '../../util/spinner.tsx';
import { consumeSubscriptionReturnOrigin } from '../../util/subscription_flow_origin.ts';
import { Button } from './button.tsx';
import { goToNextOnboardingStep, Message, Title } from './common.tsx';
import { Frame } from './frame.tsx';

export const SubscribeThankYou = () => {
  const { household } = useHousehold();
  const [completed, setCompleted] = useState(() => isInsideMobileApp());
  const [succeeded, setSucceeded] = useState(() => isInsideMobileApp());
  const onboardingPage = OnboardingPages.SubscribeThankYou;
  const navigate = useNavigate();

  useEffect(() => {
    if (completed || !household) {
      return;
    }
    void (async () => {
      const result = await connectHouseholdToStripeSubscription(household);
      setCompleted(true);
      setSucceeded(result || household.status === HouseholdStatus.Subscribed);
    })();
  }, [completed, household]);

  const onContinue = useCallback(async () => {
    await goToNextOnboardingStep(navigate, {
      from: OnboardingPages.SubscribeThankYou,
      action: 'continue',
      returnPage: consumeSubscriptionReturnOrigin(),
    });
  }, [navigate]);

  const onContactSupport = useCallback(async () => {
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.SubscribeThankYou, action: 'contactSupport' });
  }, [navigate]);

  const onStartOver = useCallback(async () => {
    await goToNextOnboardingStep(navigate, { from: OnboardingPages.SubscribeThankYou, action: 'startOver' });
  }, [navigate]);

  // No back button in any state: the page before this one is the completed Stripe checkout.
  if (!household) {
    return (
      <Frame
        onboardingPage={onboardingPage}
        suppressBackButton={true}
        mainContent={<Message $widthInChars={20}>Could not locate household.</Message>}
        footerContent={<Button onClick={onStartOver}>START OVER</Button>}
      />
    );
  }

  if (!completed) {
    return (
      <Frame
        onboardingPage={onboardingPage}
        suppressBackButton={true}
        mainContent={
          <WaitContent>
            <Message $widthInChars={20}>Please wait while we update your household.</Message>
            <Spinner />
          </WaitContent>
        }
        footerContent={<></>}
      />
    );
  }

  if (!succeeded) {
    return (
      <Frame
        onboardingPage={onboardingPage}
        suppressBackButton={true}
        mainContent={
          <>
            <Title style={{ fontSize: '2em' }}>An error occurred!</Title>
            <Message $widthInChars={22} $lessPadding={true}>
              We apologize for the incovenience. Please reach out to our support staff and we will be happy to help.
            </Message>
            <LogMessages />
          </>
        }
        footerContent={
          <>
            <Button onClick={onContactSupport}>
              {/* iOS app requires that this be an anchor rather than a button to function */}
              <ContactSupport href="mailto:support@crescendosw.com" onClick={onContinue}>
                CONTACT SUPPORT
              </ContactSupport>
            </Button>
          </>
        }
        onEnterKeypress={onContactSupport}
      />
    );
  }

  return (
    <Frame
      onboardingPage={onboardingPage}
      mainContent={
        <ThankYouContent>
          <ThankYouTitle>Thank you for partnering with us!</ThankYouTitle>
          <Message $widthInChars={20}>Let’s push music into all the corners of life together.</Message>
        </ThankYouContent>
      }
      footerContent={<Button onClick={onContinue}>CONTINUE</Button>}
      onEnterKeypress={onContinue}
      suppressBackButton={true}
    />
  );
};

// the spinner sits centered below the message
const WaitContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// The title and message have different widths; each centered on its own gave them different left
// edges. Centering them as one block with both left-aligned lines them up.
const ThankYouContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ThankYouTitle = styled(Title)`
  font-size: 2.1em;
  line-height: 120%;
  width: 15ch;
  text-wrap: wrap;
  padding-bottom: 50px;
`;

const ContactSupport = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

// UX accelerator only: the Stripe webhook is the authoritative linking mechanism (the server
// stamps the householdToken into the subscription metadata at checkout-session creation - see
// GitHub issue #3096). This call just speeds up the moment the client sees itself as subscribed.
async function connectHouseholdToStripeSubscription(household: Household): Promise<boolean> {
  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');
  if (sessionId && household?.token) {
    storeOptimisticSubscription({ householdToken: household.token, sessionId });
    // retry up to 5 times
    for (let i = 0; i < 5; i++) {
      // give Stripe time to call our server webhook, backing off more each time
      await new Promise((r) => setTimeout(r, 500 * i));

      // update newly created Stripe database entry
      await server_api.updateHouseholdWithStripeSubscription({ sessionId, householdToken: household.token });

      // TODO(hewitt): verify retry on failure -> try bogus URL to repro failure

      // does the database think we are subscribed?  If so, we're done.  Otherwise, keep trying.
      await synchronizeHouseholdWithServer({ force: true });
      if (localStorageGet(LocalStorageKey.Household)?.status === HouseholdStatus.Subscribed) {
        return true;
      }
    }
    return true;
  }
  return false;
}
